import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { NotAndroidAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { glossary } from '@sorare/core/src/lib/glossary';
import { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';

import { AuctionsActions } from 'components/auction/AuctionsActions';
import BuyField from 'components/buyActions/BuyField';
import MakeOffer from 'components/directOffer/MakeOffer';
import PrimaryBuyBuyField from 'components/primaryBuy/PrimaryBuyBuyField';
import useGetAuctionDetails from 'hooks/offers/useGetAuctionDetails';
import useGetTokenSingleSaleDetails from 'hooks/offers/useGetTokenSingleSaleDetails';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import { PlayerDrawerTokenActions_anyCard } from './__generated__/index.graphql';

type Props = {
  anyCard: PlayerDrawerTokenActions_anyCard;
  clickInstantBuyFlow?: ClickInstantBuy_Flow;
};

export const Actions = ({
  anyCard,
  clickInstantBuyFlow = ClickInstantBuy_Flow.SCOUTING_CENTER,
}: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);
  const offer = useGetTokenSingleSaleDetails(anyCard);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);

  const actions = (() => {
    if (primaryBuy) {
      return (
        <PrimaryBuyBuyField
          card={anyCard}
          size="small"
          color="quaternary"
          origin={clickInstantBuyFlow}
          primaryBuyConfirmationOptions={{
            variant: 'pro',
            actions: {
              primary: {
                label: <FormattedMessage {...glossary.gotIt} />,
              },
            },
          }}
          fullWidth
        />
      );
    }
    if (offer)
      return (
        <Horizontal>
          <NotAndroidAppFeature>
            <MakeOffer size="small" card={anyCard} />
          </NotAndroidAppFeature>
          <BuyField
            fullWidth
            card={anyCard}
            variant="small"
            color="quaternary"
          />
        </Horizontal>
      );
    if (auction && anyCard.latestEnglishAuction)
      return <AuctionsActions auction={anyCard.latestEnglishAuction} />;

    return (
      <Button size="small" fullWidth color="quaternary" disabled>
        <FormattedMessage {...glossary.transferInProgress} />
      </Button>
    );
  })();

  return actions;
};

Actions.fragments = {
  anyCard: gql`
    fragment PlayerDrawerTokenActions_anyCard on AnyCardInterface {
      slug
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
        ...AuctionsActions_auction
      }
      ...useGetTokenSingleSaleDetails_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...PrimaryBuyBuyField_anyCard
      ...BuyField_anyCard
      ...MakeOffer_anyCard
    }
    ${useGetAuctionDetails.fragments.auction}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${BuyField.fragments.anyCard}
    ${MakeOffer.fragments.anyCard}
    ${AuctionsActions.fragments.auction}
  ` as TypedDocumentNode<PlayerDrawerTokenActions_anyCard>,
};
